import { Checkbox, Chip } from '@heroui/react';
import { useState } from 'react';
import { BiTrash } from 'react-icons/bi';
import { BsEye } from 'react-icons/bs';
import { FiCalendar, FiEdit, FiExternalLink, FiMaximize2 } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { ACTIONED_BY_OPTIONS, STATE_OPTIONS } from '@/config';
import { ROUTES } from '@/config/routes';
import { AsyncNormalSelect, Button, Confirm, cn, Dialog, Select } from '@/core/ui';
import type { WeeklyRecordReview } from '@/graphql/gql/graphql';
import { useContentReview } from '@/store';
import { useGlobal } from '@/store/global';
import { getDbTypeHashFragment } from '@/utils';
import { formatDate } from '@/utils/date';

const WeeklyReviewCard = ({
  review,
  serialNo,
  isSelected,
  onSelect,
  onStateChange,
  onActionedByChange,
  onMembersChange,
  onDelete,
  deleteLoading: _deleteLoading,
}: {
  review: WeeklyRecordReview;
  serialNo: number;
  isSelected: boolean;
  onSelect: (id: string | number, selected: boolean) => void;
  onStateChange: (reviewId: string, val: number | null) => Promise<void>;
  onActionedByChange: (reviewId: string, val: number | null) => Promise<void>;
  onMembersChange: (reviewId: string, keys: Set<string> | Iterable<string>) => Promise<void>;
  onDelete: (reviewId: string | number) => Promise<void>;
  deleteLoading: boolean;
}) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const dbType = useGlobal.use.dbType();
  const openFeedbackModal = useContentReview.use.openFeedbackModal();

  const members = review.members ?? [];
  const memberIds = members.map((m) => m?.userId).filter((id): id is number => id != null);

  const stateLabel = STATE_OPTIONS.find((o) => o.key === review.state)?.label;
  const actionedByLabel = ACTIONED_BY_OPTIONS.find((o) => o.key === review.actionedBy)?.label;

  return (
    <>
      <div
        className={cn(
          'bg-content1 rounded-xl border shadow-sm flex flex-col gap-2 px-3 pt-3 pb-1 h-full transition-colors',
          isSelected ? 'border-primary-300 bg-primary-50/30' : 'border-default-200',
        )}
      >
        {/* Header */}
        <div className='flex items-center justify-between gap-2'>
          <div className='flex items-center gap-1.5 min-w-0'>
            <span className='text-xs font-mono text-default-400 shrink-0'>#{serialNo}</span>
            <Chip size='sm' variant='flat' color='default' className='text-xs font-mono shrink-0'>
              {review.id ?? '—'}
            </Chip>
            {review.createdBy && (
              <Chip size='sm' variant='flat' color='secondary' className='text-xs font-mono shrink-0'>
                by:{review.createdBy}
              </Chip>
            )}
          </div>
          <Checkbox
            size='sm'
            isSelected={isSelected}
            onValueChange={(checked) => onSelect(review.id as string | number, checked)}
            aria-label='Select weekly review'
          />
        </div>

        {/* Reason */}
        <div className='rounded-lg p-2 bg-content2 border border-default-200'>
          <code className='text-sm text-default-800 wrap-break-word whitespace-pre-wrap line-clamp-4'>
            {review.reason || '-'}
          </code>
        </div>

        {/* Record ID + date */}
        <div className='flex items-center justify-between gap-2'>
          {review.recordId != null ? (
            <Link
              target='_blank'
              rel='noopener noreferrer'
              to={
                ROUTES.CONTENT.EDIT.QUESTION.replace(':id', String(review.recordId)) +
                getDbTypeHashFragment('global-state', { dbType })
              }
              className='flex items-center gap-1 text-xs text-default-500 hover:text-primary-500'
            >
              <FiExternalLink className='size-3 shrink-0' />
              <span className='font-mono'>R: {review.recordId}</span>
            </Link>
          ) : (
            <span />
          )}
          {review.createdAt && (
            <div className='flex items-center gap-1'>
              <FiCalendar className='size-3 text-default-400 shrink-0' />
              <span className='text-xs text-default-400 whitespace-nowrap'>
                {formatDate(review.createdAt * 1000, 'DD MMM YY | HH:mm')}
              </span>
            </div>
          )}
        </div>

        {/* State + ActionedBy selects */}
        <div className='flex items-center gap-1.5'>
          <Select
            size='sm'
            variant='bordered'
            selectionMode='single'
            selectedKeys={review.state != null ? new Set([String(review.state)]) : new Set()}
            onSelectionChange={(keys) => {
              const arr = Array.from(keys as Set<string>);
              const next = arr[0] ? Number.parseInt(arr[0], 10) : NaN;
              if (Number.isFinite(next)) onStateChange(review.id?.toString() ?? '', next);
            }}
            options={STATE_OPTIONS}
            className='flex-1 text-xs'
          />
          <Select
            size='sm'
            variant='bordered'
            selectionMode='single'
            selectedKeys={review.actionedBy != null ? new Set([String(review.actionedBy)]) : new Set()}
            onSelectionChange={(keys) => {
              const arr = Array.from(keys as Set<string>);
              const next = arr[0] ? Number.parseInt(arr[0], 10) : NaN;
              if (Number.isFinite(next)) onActionedByChange(review.id?.toString() ?? '', next as number);
            }}
            options={ACTIONED_BY_OPTIONS}
            className='flex-1 text-xs'
          />
        </div>

        {/* Members */}
        <AsyncNormalSelect
          size='sm'
          variant='bordered'
          description={memberIds.length > 0 ? memberIds.join(', ') : ''}
          name='members'
          groupBy='options'
          addValuePrefix={false}
          queryKey='admins'
          selectionMode='multiple'
          selectedKeys={new Set(memberIds.map((id) => String(id)))}
          onSelectionChange={(keys) =>
            onMembersChange(review.id?.toString() ?? '', new Set(Array.from(keys).map(String)))
          }
          className='w-full text-xs'
          popoverProps={{ classNames: { content: '!p-1.5 !w-64 text-xs' } }}
          classNames={{ listbox: 'text-xs' }}
        />

        {/* Feedback */}
        <div className='relative rounded-lg p-2 bg-content2 border border-default-200'>
          <pre className='text-xs text-default-700 wrap-break-word whitespace-pre-wrap line-clamp-2 font-mono pr-14'>
            {review.comment || '---'}
          </pre>
          <div className='absolute top-1.5 right-1.5 flex items-center gap-0.5'>
            <Button
              isIconOnly
              size='sm'
              variant='faded'
              color='secondary'
              className='min-w-5 w-5 h-5'
              onPress={() => openFeedbackModal(review.id?.toString() ?? '', review.comment ?? '', 'reason')}
            >
              <BsEye className='size-3' />
            </Button>
            <Button
              isIconOnly
              size='sm'
              variant='faded'
              color='warning'
              className='min-w-5 w-5 h-5'
              onPress={() => openFeedbackModal(review.id?.toString() ?? '', review.comment ?? '', 'comment')}
            >
              <FiEdit className='size-3' />
            </Button>
          </div>
        </div>

        {/* Footer */}
        <div className='flex items-center justify-between border-t border-default-100 mt-auto py-1'>
          <div className='flex items-center gap-0.5'>
            <button
              type='button'
              title='Delete'
              onClick={() => setConfirmDelete(true)}
              className='p-1 rounded-md hover:bg-danger-50 text-danger-400 hover:text-danger-600 transition-colors'
            >
              <BiTrash className='size-3.5' />
            </button>
            <Dialog
              title='Reason'
              trigger={(onOpen) => (
                <button
                  type='button'
                  title='View full reason'
                  onClick={onOpen}
                  className='p-1 rounded-md hover:bg-primary-100 text-default-600 hover:text-primary-700 transition-colors'
                >
                  <FiMaximize2 className='size-3.5' />
                </button>
              )}
            >
              <div className='overflow-y-auto max-h-[60vh]'>
                <code className='text-sm text-default-800 wrap-break-word whitespace-pre-wrap'>
                  {review.reason}
                </code>
              </div>
            </Dialog>
          </div>

          <div className='flex items-center gap-1.5'>
            <div className='w-px h-4 bg-default-200 shrink-0' />
            {stateLabel && (
              <Chip size='sm' variant='flat' color='default' className='text-xs'>
                {stateLabel}
              </Chip>
            )}
            {actionedByLabel && actionedByLabel !== '-' && (
              <Chip size='sm' variant='flat' color='primary' className='text-xs'>
                {actionedByLabel}
              </Chip>
            )}
          </div>
        </div>
      </div>

      <Confirm
        type='danger'
        title='Delete Weekly Review'
        isOpen={confirmDelete}
        onOpenChange={(open) => {
          if (!open) setConfirmDelete(false);
        }}
        message='Are you sure you want to delete this weekly review?'
        onConfirm={async () => {
          await onDelete(review.id as string | number);
          setConfirmDelete(false);
        }}
      />
    </>
  );
};

export const WeeklyRecordReviewsGrid = ({
  rows,
  selectedIds,
  onSelect,
  onStateChange,
  onActionedByChange,
  onMembersChange,
  onDelete,
  deleteLoading,
}: {
  rows: Array<{ review: WeeklyRecordReview; serialNo: number }>;
  selectedIds: Set<string | number>;
  onSelect: (id: string | number, selected: boolean) => void;
  onStateChange: (reviewId: string, val: number | null) => Promise<void>;
  onActionedByChange: (reviewId: string, val: number | null) => Promise<void>;
  onMembersChange: (reviewId: string, keys: Set<string> | Iterable<string>) => Promise<void>;
  onDelete: (reviewId: string | number) => Promise<void>;
  deleteLoading: boolean;
}) => {
  return (
    <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 p-4 overflow-auto max-h-[calc(100vh-400px)] scrollbar-thin'>
      {rows.map(({ review, serialNo }) => (
        <WeeklyReviewCard
          key={review.id ?? `row-${review.recordId ?? serialNo}`}
          review={review}
          serialNo={serialNo}
          isSelected={selectedIds.has(review.id as string | number)}
          onSelect={onSelect}
          onStateChange={onStateChange}
          onActionedByChange={onActionedByChange}
          onMembersChange={onMembersChange}
          onDelete={onDelete}
          deleteLoading={deleteLoading}
        />
      ))}
    </div>
  );
};
